<template>
  <div
    v-show="showItems"
    :class="$style.container"
  >
    <div :class="$style.header">
      <h2>{{ headerText }}</h2>
      <p>
        These devices have recently been enrolled by users via the user portal<br>
        and have not been bound to the JumpCloud user who enrolled them.
      </p>
    </div>
    <div :class="$style.itemTableContainer">
      <ItemTable
        ref="unacknowledgedSystems"
        :boundItemsUrl="boundItemsUrl"
        :columns="columns"
        :details="true"
        :handleDetailsButtonClick="handleDetailsButtonClick"
        :handleRowClick="handleRowClick"
        :inDetailsAsidePanel="false"
        :inMainPanel="true"
        :isAsideOpen="isAsideOpen"
        :isItemLocked="isItemLocked"
        :itemType="itemTableType"
        :keepFresh="true"
        :parentOS="parentOS"
        :route="route"
        :selectedModelId="selectedModelId"
        :setSelectedModelId="setSelectedModelId"
        :showActionBar="false"
        :showBulkAction="false"
        :showSearchForm="false"
        :viewport="viewport"
      />
    </div>
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import { get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { notify } from 'client/util';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import BindToUserButton from 'client/components/ItemTable/BodyComponents/BindToUserButton.vue';
import BindToUserConfirmationModal from 'client/systems/BindToUserConfirmationModal.vue';
import BindToUserTutorialModal from 'client/systems/BindToUserTutorialModal.vue';
import DirectoryGroupingApi from 'client/api/v2/DirectoryGroupingApi';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import ItemTable from 'components/ItemTable/ItemTable.vue';
import ItemTableIndirectBindingMixin from 'components/ItemTable/mixins/ItemTableIndirectBindingMixin';
import ItemTableMethodsMixin from 'components/ItemTable/mixins/ItemTableMethodsMixin';
import ItemTablePropsMixin from 'components/ItemTable/mixins/ItemTablePropsMixin';
import ModalEventBus from 'components/Modal/ModalEventBus';
import pluralize from 'pluralize';

const {
  BodyComponentConsts,
  DirectoryGroupingConsts,
  ItemTableConsts,
  ItemTypeConsts,
  ItemTypeRoutes,
  LocalStorageConsts,
  StoreModuleMap,
} = Constants;

const systemUnacknowledgedCollection = StoreModuleMap[ItemTypeConsts.systemUnacknowledged];

const { columnTypes } = ItemTableConsts;
const { bindSystemToUserModalAcknowledged } = LocalStorageConsts;

const { success, error } = Constants.default;

export default {
  name: 'UnacknowledgedSystemItemTable',

  components: {
    ItemTable,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
    ItemTableIndirectBindingMixin,
    ItemTableMethodsMixin,
    ItemTablePropsMixin,
  ],

  props: {
    itemType: {
      type: String,
      default: ItemTypeConsts.device,
    },
    panelType: {
      default: ItemTableConsts.panelType.mainPanel,
      type: String,
    },
    showRecoveryKeyButton: {
      default: false,
      type: Boolean,
    },
  },

  data() {
    const collectionFilters = [{ name: 'acknowledged', operator: 'eq', value: 'false' }];

    const columns = [
      {
        bodyComponent: 'StatusIcon',
        bodyComponentProps: {
          type: BodyComponentConsts.statusIconTypes.system,
        },
        classNames: {
          body: [columnTypes.statusColumn],
          heading: [columnTypes.statusColumn, this.$style.itemTableContainerHeading],
        },
        headingText: 'Status',
      }, {
        bodyComponent: 'ItemName',
        bodyComponentProps: {
          titleField: 'displayName',
          subtitleField: 'amazonInstanceID',
        },
        classNames: {
          body: [columnTypes.nameColumn],
          heading: [columnTypes.nameColumn, this.$style.itemTableContainerHeading],
        },
        headingText: 'Device Name',
      }, {
        bodyComponent: 'ItemName',
        bodyComponentProps: {
          titleFunction: item => this.getInstalledBy(item),
        },
        classNames: {
          body: [columnTypes.nameColumn],
          heading: [columnTypes.nameColumn, this.$style.itemTableContainerHeading],
        },
        headingTooltipText: 'This is the JumpCloud Username of the user that installed the agent.',
        headingText: 'Installed By',
        showHeadingTooltip: true,
      }, {
        bodyComponent: 'SystemOSIcon',
        classNames: {
          body: [columnTypes.osColumn],
          heading: [columnTypes.osColumn, this.$style.itemTableContainerHeading],
        },
        headingText: 'OS',
      }, {
        bodyComponent: BindToUserButton,
        classNames: {
          body: [columnTypes.osColumn],
          heading: [columnTypes.osColumn, this.$style.itemTableContainerHeading],
        },
        bodyComponentProps: {
          handleClick: this.handleBindToUserClick,
          buttonText: 'bind',
        },
        headingTooltipText: 'If you bind to a user that self-installed the agent on their system, they are able to log in to that system.',
        headingText: 'Bind to User',
        showHeadingTooltip: true,
      }, {
        bodyComponent: 'DismissButton',
        bodyComponentProps: {
          handleClick: this.handleDismissClick,
          buttonText: 'dismiss',
        },
        classNames: {
          heading: [this.$style.itemTableContainerHeading],
        },
      },
    ];

    return {
      bindSystemToUserModalAcknowledged: '',
      collectionFilters,
      columns,
      inMainPanel: true,
      itemTableType: ItemTypeConsts.systemUnacknowledged,
      memberType: DirectoryGroupingConsts.memberTypes.user,
      provisioners: [],
      route: ItemTypeRoutes[this.itemType],
    };
  },

  computed: {
    ...mapGetters(systemUnacknowledgedCollection, {
      items: 'getCollection',
      totalCount: 'getTotalCount',
    }),

    headerText() {
      return `${this.totalCount} User Enrolled ${pluralize('Devices', this.totalCount)}`;
    },

    showItems: {
      get() {
        return this.totalCount > 0;
      },
    },

    systemUnacknowledgedCollection: () => systemUnacknowledgedCollection,
  },

  watch: {
    async items(newItems, oldItems) {
      // only update the provisioners if the items have changed
      if (Array.isArray(newItems) && newItems.length && (!Array.isArray(oldItems)
        || (Array.isArray(oldItems) && oldItems.length !== newItems.length))) {
        this.provisioners = [];

        newItems.map(async (item) => {
          if (!this.getInstalledBy(item)) { // only fetch it if we need it
            const provisioner = await this.fetchInstalledBy(item);
            this.provisioners.push(provisioner);
          }
        });
      }
    },
  },

  created() {
    // eslint-disable-next-line max-len
    this.bindSystemToUserModalAcknowledged = localStorage.getItem(bindSystemToUserModalAcknowledged);
  },

  methods: {
    ...mapActions(systemUnacknowledgedCollection, [
      'acknowledgeSystem',
      'fetchInstalledBy',
      'refreshCollection',
      'updateItems',
    ]),

    bindSystemToUser(item, username) {
      // provisionerId on the system used to bind said system to systemuser
      // provisionerId = systemuser id
      const systemuserId = get(item, 'provisionMetadata.provisioner.provisionerId', '');
      if (systemuserId === '') return;
      const parent = { type: ItemTypeConsts.system, id: item.id };
      const target = { type: ItemTypeConsts.user, id: systemuserId };
      DirectoryGroupingApi
        .bind(parent, 'add', target)
        .then(() => {
          notify(success, `${username} bound to system`);
          this.acknowledgeSystem({ id: item.id }).then(() => {
            EventHelper.triggerSystemBound({ systemId: item.id });
            this.refreshCollection();
          });
        })
        .catch(() => {
          notify(error, 'Unable to create direct binding!');
        })
        .finally(() => {
          ModalEventBus.close();
        });
    },

    getInstalledBy(item) {
      if (Array.isArray(this.provisioners) && this.provisioners.length) {
        const systemuserId = get(item, 'provisionMetadata.provisioner.provisionerId', '');
        const provisioner = this.provisioners.find(p => p.systemuserId === systemuserId);

        return provisioner ? provisioner.username : '';
      }

      return '';
    },

    handleBindToUserClick(e, item) {
      e.preventDefault();
      e.stopPropagation();
      const systemName = item.hostname;
      const username = this.getInstalledBy(item);

      if (this.bindSystemToUserModalAcknowledged) {
        ModalEventBus.open({
          component: BindToUserConfirmationModal,
          componentProps: {
            handleMainAction: () => this.bindSystemToUser(item, username),
            systemName,
            username,
          },
          isLargeModal: true,
        });
      } else {
        ModalEventBus.open({
          component: BindToUserTutorialModal,
          componentProps: {
            handleMainAction: () => this.setBindSystemToUserModal(true, item, username),
            handleClose: () => this.setBindSystemToUserModal(false, item, username),
          },
          isLargeModal: true,
        });
      }
    },

    handleDismissClick(e, item) {
      e.preventDefault();
      e.stopPropagation();

      return this.acknowledgeSystem({ id: item.id }).then(() => {
        EventHelper.triggerSystemAcknowledged({ systemId: item.id });

        this.refreshCollection();
      });
    },

    setBindSystemToUserModal(shouldOpenModal, item, username) {
      localStorage.setItem(bindSystemToUserModalAcknowledged, 'acknowledged');
      // eslint-disable-next-line max-len
      this.bindSystemToUserModalAcknowledged = 'acknowledged';

      const systemName = item.hostname;

      if (shouldOpenModal) {
        ModalEventBus.open({
          component: BindToUserConfirmationModal,
          componentProps: {
            handleMainAction: () => this.bindSystemToUser(item, username),
            systemName,
            username,
          },
          isLargeModal: true,
        });
      } else {
        ModalEventBus.close();
      }
    },
  },
};
</script>

<style module>
@value (gradientLight3) from '~css/colors.css';

.container {
  background: gradientLight3;
  height: 100%;
  padding: 20px 20px 40px;
  width: 100%;
}

.header {
  text-align: center;
}

.header h2 {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 0;
}

.itemTableContainer {
  height: 100%;
  overflow: auto;
  padding-bottom: 5rem;
  width: 100%;
}

.itemTableContainerHeading.itemTableContainerHeading {
  background: gradientLight3;
}

:global(.UnacknowledgedSystemItemTable__itemTableContainer .ItemTable__tableHeadCell) {
  background: gradientLight3;
}
</style>
